// A thesis's revisions in words (spec §3: ThesisRevision — each changed
// field before and after, who, when and why; the first revision is every
// field the thesis began with, from null).

/** The fields a revision can name, in the order a thesis reads. */
const FIELDS = [
  ["slug", "Slug"],
  ["title", "Title"],
  ["status", "Status"],
  ["claim", "Claim"],
  ["mechanism", "Mechanism"],
  ["winners_losers", "Winners and losers"],
  ["falsifier", "Falsifier"],
  ["discussion", "Discussion"],
];
const LABELS = new Map(FIELDS);
const ORDER = new Map(FIELDS.map(([key], i) => [key, i]));

export function fieldLabel(field) {
  return LABELS.get(field) ?? field;
}

/** A revision's changes in the thesis's own order: `[{field, label, before, after}]`. */
export function revisionChanges(revision) {
  const changes = revision?.changes && typeof revision.changes === "object" ? revision.changes : {};
  return Object.entries(changes)
    .map(([field, change]) => ({
      field,
      label: fieldLabel(field),
      before: change?.before ?? null,
      after: change?.after ?? null,
    }))
    .sort((a, b) => (ORDER.get(a.field) ?? 99) - (ORDER.get(b.field) ?? 99));
}

/** Lowercase names joined as a reader says them: "the claim and the falsifier". */
function joined(names) {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * What a revision did, in one line: "Written" (the first — every field
 * from nothing), "Retired" / "Revived" (a status change, a person's), or
 * "Changed the claim and the falsifier".
 */
export function revisionHeadline(revision) {
  const changes = revisionChanges(revision);
  if (changes.length > 0 && changes.every((c) => c.before === null) && changes.some((c) => c.field === "slug")) {
    return "Written";
  }
  const status = changes.find((c) => c.field === "status");
  const rest = changes.filter((c) => c.field !== "status").map((c) => `the ${c.label.toLowerCase()}`);
  const parts = [];
  if (status) parts.push(status.after === "retired" ? "Retired" : "Revived");
  if (rest.length) parts.push(`${parts.length ? "changed" : "Changed"} ${joined(rest)}`);
  return parts.join(", and ") || "No change recorded";
}

/** A revision value as text: a discussion by its number, a status in words, "none" for nothing. */
export function revisionValue(field, value) {
  if (value === null || value === undefined || value === "") return null;
  if (field === "discussion") return `Discussion #${value}`;
  if (field === "status") return value === "retired" ? "Retired" : "Active";
  return String(value);
}
